/**
 * LinkedResolutionFlow — the linked-checklist half of the merged Process Migration wizard.
 *
 * Mounted by WorkflowDetailPage after the target step. It resolves the LINKED_CHECKLIST
 * dependency tree for the selected checklists; when the tree has NO linked children it calls
 * `onNoLinkedChildren()` so the wizard falls back to the plain review/import path. Otherwise it
 * drives three stages — resolve (per-child decisions) → review → done (outcomes + pipeline) —
 * and runs the migration through POST /api/linked-checklist/migrate (revision-aware via
 * `targetChecklistId`).
 *
 * Ported from the LinkedProcessMigrationPrototype page (steps 3–5); the Leucine-branded styling
 * stays scoped under `.leu-scope` (linked-process-migration.css) so its oklch tokens never
 * collide with the app's global shadcn tokens.
 */
import React, { useEffect, useMemo, useRef, useState, type CSSProperties } from "react";
import "./linked-process-migration.css";

import {
  useInstanceFacilities,
  useInstanceUseCases,
  useChecklistSearch,
  useLinkedChecklistResolve,
  useLinkedChecklistMigrate,
  useLinkedChecklistOntologyPreview,
  type ChecklistSearchItem,
  type LinkedChecklistNode,
  type LinkedChecklistRef,
  type LinkedChecklistDecision,
  type LinkedChecklistMigrationOutcome,
  type LinkedChecklistOntologyGroup,
  type LinkedMigrationPipelineEntry,
  type OntologyRecord,
  type OntologySyncStatus,
} from "../../../lib/api-central-mu";
import { useToast } from "../../../lib/use-toast";
import LinkedPipelinePanel from "./LinkedPipelinePanel";
import { Check, ChevronRight, ArrowRight, ArrowLeft, SearchIco, Plus, ListIco, Alert, Refresh, Clock, Package, LinkIco } from "./icons";

export type LinkedStage = "resolve" | "review" | "done";

type ActionEntry = {
  action: "matched" | "accept" | "link" | "migrate";
  linkedTo?: LinkedChecklistRef;
  /** "migrate" only: the target use case the child imports into (within the parent's target
   *  facility — a process cannot link checklists across facilities). Explicit per-child choice. */
  targetUseCaseId?: string;
};

interface Props {
  sourceServiceId: number;
  sourceFacilityId: string;
  /** SOURCE use case the parents were listed under — audits render its name. */
  sourceUseCaseId?: string;
  /** Selected parent checklists (id + display name/code). */
  checklists: { id: string; name: string; code?: string }[];
  targetServiceId: number;
  targetFacilityId: string;
  targetUseCaseId?: string;
  targetAuthorId?: string;
  targetAuthorLabel?: string;
  /** When set, the (single) selected checklist imports as a REVISION of this target checklist. */
  targetChecklistId?: string;
  /** User-entered workflow description — shown on the migration-logs header of each tracking workflow. */
  description?: string;
  /** Resolve found no linked children — the wizard continues on the plain import path. */
  onNoLinkedChildren: () => void;
  /** Back from the resolve stage → the wizard's target step. */
  onBack: () => void;
  /** Start-over from the done stage. */
  onStartOver: () => void;
  /** Stage transitions, for the wizard's outer stepper. */
  onStageChange?: (stage: LinkedStage) => void;
  /**
   * Mixed selection: the wizard continues to the standard (non-linked) group's plain import
   * after this flow — rendered as a primary continue button on the done stage.
   */
  continueStandard?: { count: number; onContinue: () => void };
}

const uppercaseLbl: CSSProperties = { fontSize: 10.5, letterSpacing: "0.06em", textTransform: "uppercase", color: "var(--fg-3)", fontWeight: 500 };

/**
 * Per-child use-case picker for a "Migrate" decision (explicit choice, no default). The facility is
 * NOT selectable: a process cannot link checklists across facilities, so a migrated child always
 * lands in the parent's target facility — use cases are listed from there.
 */
const MigrateTargetPicker: React.FC<{
  serviceId: number;
  facilityId: string;
  entry: ActionEntry;
  onChange: (patch: { targetUseCaseId?: string }) => void;
}> = ({ serviceId, facilityId, entry, onChange }) => {
  const { data: useCases = [] } = useInstanceUseCases(serviceId, facilityId || null);
  const selStyle: CSSProperties = { height: 28, fontSize: 12, padding: "0 6px", maxWidth: 190 };
  return (
    <select
      className="leu-input"
      style={selStyle}
      value={entry.targetUseCaseId ?? ""}
      onChange={(e) => onChange({ targetUseCaseId: e.target.value })}
      title="Target use case for this checklist (in the parent's target facility)"
    >
      <option value="" disabled>Select use case…</option>
      {useCases.map((u) => (<option key={u.id} value={u.id}>{u.name}</option>))}
    </select>
  );
};

/** Renders a use-case's name (falls back to the raw id) for a given facility. */
const UseCaseName: React.FC<{ serviceId: number; facilityId: string; useCaseId: string }> =
  ({ serviceId, facilityId, useCaseId }) => {
    const { data: useCases = [] } = useInstanceUseCases(serviceId, facilityId);
    return <>{useCases.find((u) => u.id === useCaseId)?.name ?? useCaseId}</>;
  };

const LinkedResolutionFlow: React.FC<Props> = ({
  sourceServiceId, sourceFacilityId, sourceUseCaseId, checklists,
  targetServiceId, targetFacilityId, targetUseCaseId, targetAuthorId, targetAuthorLabel,
  targetChecklistId, description,
  onNoLinkedChildren, onBack, onStartOver, onStageChange, continueStandard,
}) => {
  const [stage, setStageRaw] = useState<LinkedStage>("resolve");
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [actions, setActions] = useState<Record<string, ActionEntry>>({});
  const [tree, setTree] = useState<LinkedChecklistNode[] | null>(null);
  const [dialogFor, setDialogFor] = useState<string | null>(null);
  const [dialogSearch, setDialogSearch] = useState("");
  const [dialogUseCaseFilter, setDialogUseCaseFilter] = useState("");
  const [phase, setPhase] = useState<"idle" | "processing" | "done" | "error">("idle");
  const [outcomes, setOutcomes] = useState<LinkedChecklistMigrationOutcome[]>([]);
  const [migrateError, setMigrateError] = useState<string>("");
  // Revision target for the parent. Seeded from the target step's import-type choice; the operator
  // can also decide here on the resolution screen when a parent is already on the target (see the
  // NEW/REVISION control on the parent row). Revision maps one source checklist onto one target
  // ancestor, so it is only offered for a single-checklist selection.
  const [revisionTargetId, setRevisionTargetId] = useState<string | undefined>(targetChecklistId);
  const canRevise = checklists.length === 1;

  const setStage = (s: LinkedStage) => { setStageRaw(s); onStageChange?.(s); };

  const { data: targetUseCases = [] } = useInstanceUseCases(targetServiceId, targetFacilityId);
  const { data: targetFacilities = [] } = useInstanceFacilities(targetServiceId);
  const dialogResultsQuery = useChecklistSearch(targetServiceId, targetFacilityId, dialogSearch, !!dialogFor, dialogUseCaseFilter || null);

  const resolveMut = useLinkedChecklistResolve();
  const { toast } = useToast();
  const migrateMut = useLinkedChecklistMigrate();
  const ontologyPreviewMut = useLinkedChecklistOntologyPreview();

  /* ---- tree helpers ---- */
  const resolvableNodes = useMemo(() => {
    if (!tree) return [];
    const seen = new Set<string>();
    const out: LinkedChecklistNode[] = [];
    const walk = (nodes: LinkedChecklistNode[]) => nodes.forEach((n) => {
      if (n.status && !seen.has(n.source.id)) { seen.add(n.source.id); out.push(n); }
      if (n.children?.length) walk(n.children);
    });
    walk(tree);
    return out;
  }, [tree]);

  const visibleRows = useMemo(() => {
    if (!tree) return [] as { node: LinkedChecklistNode; hasChildren: boolean }[];
    const out: { node: LinkedChecklistNode; hasChildren: boolean }[] = [];
    const walk = (nodes: LinkedChecklistNode[]) => nodes.forEach((n) => {
      const hasChildren = !!n.children?.length;
      out.push({ node: n, hasChildren });
      if (hasChildren && expanded[n.source.id]) walk(n.children);
    });
    walk(tree);
    return out;
  }, [tree, expanded]);

  const nameLookup = useMemo(() => {
    const m = new Map<string, LinkedChecklistRef>();
    const walk = (nodes: LinkedChecklistNode[]) => nodes.forEach((n) => { m.set(n.source.id, n.source); if (n.children?.length) walk(n.children); });
    if (tree) walk(tree);
    return m;
  }, [tree]);

  const sumPresent = resolvableNodes.filter((n) => n.status === "PRESENT").length;
  const sumProposed = resolvableNodes.filter((n) => n.status === "PROPOSED_MATCH").length;
  const sumMissing = resolvableNodes.filter((n) => n.status === "MISSING").length;
  const sumUnresolved = resolvableNodes.filter((n) => !actions[n.source.id]?.action).length;
  const allResolved = sumUnresolved === 0;
  const discovering = resolveMut.isPending;
  const discovered = tree != null && !discovering;

  /* ---- checklists that will actually be migrated (parents + "migrate" children) ---- */
  const migratingChecklistIds = useMemo(() => {
    const ids = new Set<string>();
    (tree ?? []).forEach((root) => ids.add(root.source.id));
    resolvableNodes.forEach((n) => {
      if (actions[n.source.id]?.action === "migrate") ids.add(n.source.id);
    });
    return ids;
  }, [tree, resolvableNodes, actions]);

  /* ---- missing object types/objects on a checklist that's being migrated block the migration ---- */
  const missingOntologyBlocking = useMemo(() => {
    const groups = ontologyPreviewMut.data?.perChecklist ?? [];
    return groups
      .filter((g) => migratingChecklistIds.has(g.checklistId))
      .flatMap((g) => g.ontologyRecords)
      .filter((r) => r.status === "MISSING").length;
  }, [ontologyPreviewMut.data, migratingChecklistIds]);

  /* ---- nested linked children (one-level rule): can only be matched/linked, never migrated inline ---- */
  const nestedUnresolved = useMemo(
    () => resolvableNodes.filter((n) => n.hasNestedLinks && !actions[n.source.id]?.action),
    [resolvableNodes, actions],
  );

  /* ---- decisions for the migrate request ---- */
  const decisions: LinkedChecklistDecision[] = resolvableNodes.map((n) => {
    const a = actions[n.source.id];
    if (!a) return null;
    if (a.action === "matched" || a.action === "accept") {
      return { sourceChecklistId: n.source.id, action: "LINK_EXISTING", targetChecklistId: n.proposedTarget ? n.proposedTarget.id : null };
    }
    if (a.action === "link") {
      return { sourceChecklistId: n.source.id, action: "LINK_EXISTING", targetChecklistId: a.linkedTo ? a.linkedTo.id : null };
    }
    return {
      sourceChecklistId: n.source.id, action: "MIGRATE", targetChecklistId: null,
      targetUseCaseId: a.targetUseCaseId || null,
    };
  }).filter(Boolean) as LinkedChecklistDecision[];

  /* ---- every "migrate" child needs an explicit target use case before continuing ---- */
  const migrateTargetIncomplete = resolvableNodes.some((n) => {
    const a = actions[n.source.id];
    return a?.action === "migrate" && !a.targetUseCaseId;
  });

  /* ---- actions ---- */
  const setAction = (id: string, action: ActionEntry["action"]) => setActions((s) => ({ ...s, [id]: { action } }));
  const setMigrateTarget = (id: string, patch: { targetUseCaseId?: string }) =>
    setActions((s) => {
      const cur = s[id];
      if (!cur || cur.action !== "migrate") return s;
      return { ...s, [id]: { ...cur, ...patch } };
    });
  const toggleExpand = (id: string) => setExpanded((s) => ({ ...s, [id]: !s[id] }));
  const openDialog = (id: string) => { setDialogFor(id); setDialogSearch(""); };
  const closeDialog = () => { setDialogFor(null); setDialogUseCaseFilter(""); };
  const pickLinked = (item: ChecklistSearchItem) => {
    const id = dialogFor;
    const ref: LinkedChecklistRef = { id: String(item.ancestorId ?? item.id), name: item.name, code: item.code ?? "", useCaseId: String(item.useCaseId ?? "") };
    if (id) setActions((s) => ({ ...s, [id]: { action: "link", linkedTo: ref } }));
    setDialogFor(null);
  };

  const runResolve = () => {
    setActions({});
    setTree(null);
    ontologyPreviewMut.reset();
    resolveMut.mutate(
      {
        sourceServiceId, sourceFacilityId,
        checklistIds: checklists.map((c) => c.id),
        targetServiceId, targetFacilityId,
      },
      {
        onSuccess: (res) => {
          // No linked children anywhere in the tree → this is a plain process migration.
          const anyResolvable = (function walk(nodes: LinkedChecklistNode[]): boolean {
            return nodes.some((n) => !!n.status || (n.children?.length ? walk(n.children) : false));
          })(res.tree ?? []);
          if (!anyResolvable) {
            onNoLinkedChildren();
            return;
          }
          setTree(res.tree);
          // Nested linked process (more than one parent→child level): a child that itself links
          // further processes cannot be migrated in this run. Pop a clear message so the user isn't
          // left with a silently-withheld Migrate action.
          const nested: string[] = [];
          (function walkNested(nodes: LinkedChecklistNode[]) {
            nodes.forEach((n) => {
              if (n.hasNestedLinks) nested.push(n.source.name ?? n.source.id);
              if (n.children?.length) walkNested(n.children);
            });
          })(res.tree);
          if (nested.length) {
            toast({
              variant: "destructive",
              title: "Nested linked process migration is not allowed",
              description: `${nested.join(", ")} link${nested.length === 1 ? "s" : ""} further processes. Migrate ${nested.length === 1 ? "it" : "them"} individually first (and publish on the target), then link ${nested.length === 1 ? "it" : "them"} here.`,
            });
          }
          // expand every node with children; pre-resolve PRESENT children to "matched";
          // collect every distinct checklist id (parent + children) for the ontology preview.
          const exp: Record<string, boolean> = {};
          const act: Record<string, ActionEntry> = {};
          const ids: string[] = [];
          const seenIds = new Set<string>();
          const walk = (nodes: LinkedChecklistNode[]) => nodes.forEach((n) => {
            if (!seenIds.has(n.source.id)) { seenIds.add(n.source.id); ids.push(n.source.id); }
            if (n.children?.length) exp[n.source.id] = true;
            if (n.status === "PRESENT") act[n.source.id] = { action: "matched" };
            if (n.children?.length) walk(n.children);
          });
          walk(res.tree);
          setExpanded(exp);
          setActions(act);
          // Ontology dependency preview for the WHOLE tree — including already-present children — so
          // each process shows its object types/objects as MATCH / MISSING / CONFLICT on the target.
          if (ids.length) {
            ontologyPreviewMut.mutate({
              sourceServiceId, sourceFacilityId, targetServiceId, targetFacilityId,
              checklistIds: ids, includeObjects: true,
            });
          }
        },
      }
    );
  };

  // Resolve once on mount (StrictMode-guarded).
  const resolvedOnce = useRef(false);
  useEffect(() => {
    if (!resolvedOnce.current) {
      resolvedOnce.current = true;
      runResolve();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * A deferred parent finished importing via the pipeline panel's "Continue migration" — flip its
   * stale "Deferred: publish…" outcome row to the real result so the done screen shows success.
   */
  const handlePipelineEntryResolved = (entry: LinkedMigrationPipelineEntry) => {
    if (entry.status !== "MIGRATED") return;
    setOutcomes((prev) => prev.map((o) =>
      o.sourceId === entry.parentChecklistId && o.status === "PENDING_CHILD_PUBLISH"
        ? { ...o, status: "SUCCESS", targetChecklistId: entry.parentChecklistId, message: "Imported checklist to target" }
        : o));
  };

  const startMigration = () => {
    setStage("done"); setPhase("processing"); setMigrateError("");
    migrateMut.mutate(
      {
        sourceServiceId, sourceFacilityId, checklistIds: checklists.map((c) => c.id),
        targetServiceId, targetFacilityId,
        targetAuthorIds: targetAuthorId || undefined, targetUseCaseId: targetUseCaseId || undefined,
        decisions,
        targetChecklistId: revisionTargetId || undefined,
        sourceUseCaseId: sourceUseCaseId || undefined,
        description: description || undefined,
      },
      {
        onSuccess: (res) => { setOutcomes(res.outcomes ?? []); setPhase("done"); },
        onError: (e) => { setMigrateError(e instanceof Error ? e.message : "Migration failed"); setPhase("error"); },
      }
    );
  };

  const seg = (active: boolean): CSSProperties => {
    const b: CSSProperties = { flex: "0 0 auto", padding: "5px 10px", borderRadius: "7px", fontSize: "12px", cursor: "pointer", border: 0, whiteSpace: "nowrap", fontFamily: "inherit", transition: "all 120ms" };
    return active
      ? { ...b, fontWeight: 600, background: "var(--card)", color: "var(--fg-1)", boxShadow: "var(--shadow-xs)" }
      : { ...b, fontWeight: 500, background: "transparent", color: "var(--fg-3)" };
  };

  /* ---- ontology dependency preview (per checklist: object types / objects vs target) ---- */
  const ontologyStatusMeta = (status: OntologySyncStatus | null): { label: string; cls: string } => {
    switch (status) {
      case "MATCH": return { label: "Match", cls: "leu-badge leu-badge--success" };
      case "MISSING": return { label: "Missing", cls: "leu-badge leu-badge--destructive" };
      case "CONFLICT": return { label: "Conflict", cls: "leu-badge leu-badge--warning" };
      case "SYNC_FAILED": return { label: "Sync failed", cls: "leu-badge" };
      default: return { label: "Unknown", cls: "leu-badge" };
    }
  };
  const ontologyTypeLabel = (type: OntologyRecord["type"]): string => {
    switch (type) {
      case "OBJECT_TYPE": return "Object type";
      case "OBJECT": return "Object";
      case "RELATION": return "Relation";
      case "PROPERTY": return "Property";
      case "PROPERTY_OPTIONS": return "Option";
      default: return type;
    }
  };
  const renderOntologyPanel = () => {
    const data = ontologyPreviewMut.data;
    const groups: LinkedChecklistOntologyGroup[] = data?.perChecklist ?? [];
    const agg: OntologyRecord[] = data?.aggregated ?? [];
    const count = (s: OntologySyncStatus) => agg.filter((r) => r.status === s).length;
    return (
      <div className="leu-card" style={{ overflow: "hidden", marginTop: 16 }}>
        <div style={{ padding: "11px 18px", borderBottom: "1px solid var(--border)", display: "flex", alignItems: "center", justifyContent: "space-between", background: "var(--bg-2)" }}>
          <span style={uppercaseLbl}>Ontology dependencies</span>
          {ontologyPreviewMut.isSuccess && (
            <span style={{ fontSize: 11.5, display: "inline-flex", gap: 12 }}>
              <span style={{ color: "var(--success)" }}>{count("MATCH")} match</span>
              <span style={{ color: "var(--warning, #b45309)" }}>{count("CONFLICT")} conflict</span>
              <span style={{ color: "var(--destructive)" }}>{count("MISSING")} missing</span>
            </span>
          )}
        </div>
        {ontologyPreviewMut.isPending && (<div style={{ padding: 24, textAlign: "center", color: "var(--fg-3)", fontSize: 13 }}>Checking object types &amp; objects on the target…</div>)}
        {ontologyPreviewMut.isError && (<div style={{ padding: 20, textAlign: "center", color: "var(--destructive)", fontSize: 12.5 }}>Could not load ontology dependencies.</div>)}
        {ontologyPreviewMut.isSuccess && groups.length === 0 && (<div style={{ padding: 24, textAlign: "center", color: "var(--fg-3)", fontSize: 13 }}>No ontology dependencies found.</div>)}
        {ontologyPreviewMut.isSuccess && groups.map((g) => (
          <div key={g.checklistId} style={{ borderBottom: "1px solid var(--border)" }}>
            <div style={{ padding: "10px 18px", display: "flex", alignItems: "center", gap: 8 }}>
              <span style={{ fontSize: 12.5, fontWeight: 600 }}>{(() => {
                const ref = nameLookup.get(g.checklistId);
                const name = ref?.name ?? g.checklistName;
                // Never surface the raw backend checklist id as a name — fall back to the code, then a
                // neutral label. The id is still shown (in mono) alongside for reference.
                if (name && name !== g.checklistId) return name;
                return ref?.code || "Linked checklist";
              })()}</span>
              <span style={{ fontFamily: "var(--font-mono)", fontSize: 10.5, color: "var(--fg-4)" }}>{g.checklistId}</span>
              <span style={{ fontSize: 11, color: "var(--fg-4)" }}>· {g.ontologyRecords.length} dependenc{g.ontologyRecords.length === 1 ? "y" : "ies"}</span>
            </div>
            {g.ontologyRecords.length === 0 ? (
              <div style={{ padding: "0 18px 12px 30px", fontSize: 11.5, color: "var(--fg-4)" }}>No object types or objects referenced.</div>
            ) : (
              g.ontologyRecords.map((r, i) => {
                const meta = ontologyStatusMeta(r.status);
                return (
                  <div key={(r.id ?? "") + ":" + r.type + ":" + i} style={{ display: "flex", alignItems: "center", gap: 10, padding: "7px 18px 7px 30px" }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <span style={{ fontSize: 12.5, color: "var(--fg-1)" }}>{r.displayName ?? r.id}</span>
                      <span style={{ fontSize: 10.5, color: "var(--fg-4)", marginLeft: 8 }}>{ontologyTypeLabel(r.type)}{r.objectTypeName && r.type !== "OBJECT_TYPE" ? " · " + r.objectTypeName : ""}</span>
                    </div>
                    <span className={meta.cls} style={{ gap: 6 }}><span className="leu-badge-dot" />{meta.label}</span>
                  </div>
                );
              })
            )}
          </div>
        ))}
      </div>
    );
  };

  /* ---- row renderer (resolve stage) ---- */
  const renderRow = (n: LinkedChecklistNode, hasChildren: boolean) => {
    const exp = !!expanded[n.source.id];
    const isParent = n.status == null;
    const a = actions[n.source.id];
    const nameStyle: CSSProperties = isParent
      ? { fontWeight: 600, fontSize: 14, color: "var(--fg-1)" }
      : { fontWeight: 500, fontSize: 13, color: "var(--fg-1)" };
    const t = n.proposedTarget;

    const statusLabel = n.status === "PRESENT" ? "Present" : n.status === "PROPOSED_MATCH" ? "Proposed match" : "Missing";
    const badgeClass = n.status === "PRESENT" ? "leu-badge leu-badge--success" : n.status === "PROPOSED_MATCH" ? "leu-badge leu-badge--warning" : "leu-badge leu-badge--destructive";

    const segs: { key: ActionEntry["action"]; label: string }[] = [];
    if (n.status === "PRESENT") segs.push({ key: "matched", label: "Use matched" });
    if (n.status === "PROPOSED_MATCH") segs.push({ key: "accept", label: "Accept proposed" });
    segs.push({ key: "link", label: "Link existing…" });
    // Migrate is always offered — even for an already-migrated child (DWI export/import parity:
    // streem creates a fresh copy on the target when the id already exists there) — EXCEPT for a
    // child that itself links other processes: only one parent→child level can migrate together.
    if (!n.hasNestedLinks) segs.push({ key: "migrate", label: "Migrate" });

    let line = "Action required", tone: "ok" | "info" | "req" = "req";
    if (a?.action === "matched") { line = "Matched: " + (t?.name ?? "") + " · " + (t?.code ?? ""); tone = "ok"; }
    else if (a?.action === "accept") { line = "Linked to " + (t?.name ?? "") + " · " + (t?.code ?? ""); tone = "ok"; }
    else if (a?.action === "link") { line = "Linked to " + (a.linkedTo?.name ?? "") + " · " + (a.linkedTo?.code ?? ""); tone = "ok"; }
    else if (a?.action === "migrate") {
      const complete = !!a.targetUseCaseId;
      line = complete ? "Will be created on target" : "Select the target use case";
      tone = complete ? "info" : "req";
    }
    const lineStyle: CSSProperties = { fontWeight: 500, fontSize: 11.5, color: tone === "ok" ? "var(--success)" : tone === "info" ? "var(--brand-600)" : "var(--destructive)" };
    const toneIcon = tone === "ok" ? <span style={{ display: "inline-flex", color: "var(--success)" }}><Check size={13} stroke={3} /></span>
      : tone === "info" ? <span style={{ display: "inline-flex", color: "var(--brand-600)" }}><Plus size={13} /></span>
        : <span style={{ display: "inline-flex", color: "var(--destructive)" }}><Alert size={13} /></span>;

    const segmentBar = (
      <div style={{ display: "inline-flex", background: "var(--bg-3)", padding: 3, borderRadius: 8, gap: 2 }}>
        {segs.map((g) => (<button key={g.key} style={seg(a?.action === g.key)} onClick={() => (g.key === "link" ? openDialog(n.source.id) : setAction(n.source.id, g.key))}>{g.label}</button>))}
      </div>
    );
    const resultLineEl = (<div style={{ display: "flex", alignItems: "center", gap: 6 }}>{toneIcon}<span style={lineStyle}>{line}</span></div>);
    const nestedWarningEl = n.hasNestedLinks ? (
      <div style={{ display: "flex", alignItems: "flex-start", gap: 6, maxWidth: 394 }}>
        <span style={{ display: "inline-flex", color: "var(--warning, #b45309)", marginTop: 1 }}><Alert size={13} /></span>
        <span style={{ fontSize: 11, color: "var(--fg-3)", textAlign: "right" }}>
          This process links other processes — nested linked migration supports only one level. Migrate it individually first, then link it here.
        </span>
      </div>
    ) : null;
    const migratePickerEl = a?.action === "migrate" ? (
      <MigrateTargetPicker
        serviceId={targetServiceId}
        facilityId={targetFacilityId}
        entry={a}
        onChange={(patch) => setMigrateTarget(n.source.id, patch)}
      />
    ) : null;
    const rowStyle: CSSProperties = a?.action ? { background: "transparent" } : { background: "color-mix(in oklch, var(--warning) 5%, transparent)" };
    const panelStyle: CSSProperties = { flex: "none", width: 430, display: "flex", flexDirection: "column", gap: 7, alignItems: "flex-end", justifyContent: "center", padding: "11px 18px" };

    return (
      <div key={n.source.id} style={{ ...(isParent ? {} : rowStyle), display: "flex", alignItems: "stretch", borderBottom: "1px solid var(--border)" }}>
        {Array.from({ length: n.depth }).map((_, i) => (<span key={i} style={{ width: 26, flex: "none", alignSelf: "stretch", boxShadow: "inset 1px 0 0 var(--border)" }} />))}
        <div style={{ flex: 1, display: "flex", alignItems: "center", gap: 8, padding: "13px 8px 13px 10px", minWidth: 0 }}>
          <span style={{ width: 18, flex: "none", display: "inline-flex", alignItems: "center", justifyContent: "center" }}>
            {hasChildren && (
              <button onClick={() => toggleExpand(n.source.id)} style={{ display: "inline-flex", color: "var(--fg-3)", transition: "transform 150ms var(--ease-standard)", transform: exp ? "rotate(90deg)" : "none", background: "transparent", border: 0, cursor: "pointer", padding: 0 }}>
                <ChevronRight />
              </button>
            )}
          </span>
          <div style={{ minWidth: 0, flex: 1 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span style={nameStyle}>{n.source.name}</span>
              <span style={{ fontFamily: "var(--font-mono)", fontSize: 11, color: "var(--fg-3)" }}>{n.source.code}</span>
              {isParent && <span className="leu-badge leu-badge--secondary" style={{ height: 19, fontSize: 10 }}>Parent process</span>}
              {isParent && revisionTargetId && <span className="leu-badge leu-badge--info" style={{ height: 19, fontSize: 10 }}>Revision</span>}
              {n.alreadyMigrated && <span className="leu-badge leu-badge--success" style={{ height: 19, fontSize: 10, gap: 4 }}><Check size={10} stroke={3} />Already migrated</span>}
            </div>
            {n.source.useCaseId && <div style={{ fontSize: 11, color: "var(--fg-4)", marginTop: 2 }}>Use case · {n.source.useCaseId}</div>}
          </div>
        </div>

        {isParent && (
          <div style={{ flex: "none", width: 300, display: "flex", alignItems: "center", justifyContent: "flex-end", padding: "0 18px" }}>
            {n.alreadyMigrated && n.proposedTarget && canRevise ? (
              <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 5 }}>
                <div style={{ display: "inline-flex", gap: 4, background: "var(--bg-2)", padding: 3, borderRadius: 9 }}>
                  <button type="button" style={seg(!revisionTargetId)} onClick={() => setRevisionTargetId(undefined)}>New copy</button>
                  <button type="button" style={seg(!!revisionTargetId)} onClick={() => setRevisionTargetId(n.proposedTarget!.id)}>Revise existing</button>
                </div>
                <span style={{ fontSize: 10.5, color: "var(--fg-4)", textAlign: "right" }}>
                  {revisionTargetId
                    ? <>Revises <span style={{ color: "var(--fg-2)" }}>{n.proposedTarget.name}</span> <span style={{ fontFamily: "var(--font-mono)" }}>{n.proposedTarget.code}</span></>
                    : "Creates a new checklist on target"}
                </span>
              </div>
            ) : (
              <span style={{ color: "var(--fg-4)", fontSize: 11.5, textAlign: "right" }}>{revisionTargetId ? "Imported as a revision on target" : n.alreadyMigrated ? "Already on target — migrating creates a new copy" : "Anchor of the migration"}</span>
            )}
          </div>
        )}

        {n.status === "PRESENT" && (
          <div style={panelStyle}>
            <span className={badgeClass} style={{ gap: 6 }}><span className="leu-badge-dot" />{statusLabel}</span>
            {a?.action === "matched" && t && (<div style={{ fontSize: 11, color: "var(--fg-3)", textAlign: "right" }}>Matched on target · <span style={{ color: "var(--fg-2)", fontWeight: 500 }}>{t.name}</span> <span style={{ fontFamily: "var(--font-mono)", color: "var(--fg-3)" }}>{t.code}</span></div>)}
            {nestedWarningEl}
            {segmentBar}
            {migratePickerEl}
            {a?.action && a.action !== "matched" && resultLineEl}
          </div>
        )}

        {n.status === "PROPOSED_MATCH" && (
          <div style={panelStyle}>
            <span className={badgeClass} style={{ gap: 6 }}><span className="leu-badge-dot" />{statusLabel}</span>
            <div style={{ display: "flex", alignItems: "stretch", gap: 8 }}>
              <div style={{ display: "flex", flexDirection: "column", padding: "5px 9px", border: "1px solid var(--border)", borderRadius: 7, background: "var(--bg-2)" }}>
                <span style={{ fontSize: 9, textTransform: "uppercase", letterSpacing: "0.05em", color: "var(--fg-4)" }}>Source</span>
                <span style={{ fontSize: 11, color: "var(--fg-2)" }}>{n.source.name}</span>
                <span style={{ fontFamily: "var(--font-mono)", fontSize: 10, color: "var(--fg-3)" }}>{n.source.code}</span>
              </div>
              <span style={{ display: "flex", alignItems: "center", color: "var(--fg-4)" }}><ArrowRight stroke={1.75} /></span>
              <div style={{ display: "flex", flexDirection: "column", padding: "5px 9px", border: "1px solid color-mix(in oklch, var(--warning) 45%, var(--border))", borderRadius: 7, background: "color-mix(in oklch, var(--warning) 10%, var(--bg-1))" }}>
                <span style={{ fontSize: 9, textTransform: "uppercase", letterSpacing: "0.05em", color: "oklch(0.5 0.14 70)" }}>Proposed</span>
                <span style={{ fontSize: 11, color: "var(--fg-1)", fontWeight: 500 }}>{t?.name}</span>
                <span style={{ fontFamily: "var(--font-mono)", fontSize: 10, color: "var(--fg-3)" }}>{t?.code}</span>
              </div>
            </div>
            {nestedWarningEl}
            {segmentBar}
            {migratePickerEl}
            {resultLineEl}
          </div>
        )}

        {n.status === "MISSING" && (
          <div style={panelStyle}>
            <span className={badgeClass} style={{ gap: 6 }}><span className="leu-badge-dot" />{statusLabel}</span>
            <div style={{ fontSize: 11, color: "var(--fg-3)" }}>Not found on target</div>
            {nestedWarningEl}
            {segmentBar}
            {migratePickerEl}
            {resultLineEl}
          </div>
        )}
      </div>
    );
  };

  /* ---- done-stage result icon by status ---- */
  const resultIcon = (status: string) => {
    if (status === "FAILED") return <span style={{ width: 20, height: 20, borderRadius: "50%", background: "color-mix(in oklch, var(--destructive) 14%, var(--card))", color: "var(--destructive)", display: "inline-flex", alignItems: "center", justifyContent: "center", flex: "none" }}><Alert size={12} /></span>;
    if (status === "PENDING_CHILD_PUBLISH") return <span style={{ width: 20, height: 20, borderRadius: "50%", background: "color-mix(in oklch, var(--warning, #b45309) 16%, var(--card))", color: "var(--warning, #b45309)", display: "inline-flex", alignItems: "center", justifyContent: "center", flex: "none" }}><Clock size={12} /></span>;
    if (status === "SKIPPED") return <span style={{ width: 20, height: 20, borderRadius: "50%", background: "var(--bg-3)", color: "var(--fg-3)", display: "inline-flex", alignItems: "center", justifyContent: "center", flex: "none" }}>–</span>;
    return <span style={{ width: 20, height: 20, borderRadius: "50%", background: "color-mix(in oklch, var(--success) 14%, var(--card))", color: "var(--success)", display: "inline-flex", alignItems: "center", justifyContent: "center", flex: "none" }}><Check size={13} stroke={3} /></span>;
  };

  /* ---- footer gating ---- */
  const continueDisabled = stage === "resolve"
    && (!allResolved || migrateTargetIncomplete || discovering || !discovered || ontologyPreviewMut.isPending || missingOntologyBlocking > 0);

  return (
    <div className="leu-scope" style={{ background: "var(--bg-2)", borderRadius: 12 }}>
      <div style={{ maxWidth: 1152, margin: "0 auto", padding: "4px 0 12px" }}>

        {/* ============ RESOLVE ============ */}
        {stage === "resolve" && (
          <div>
            <div style={{ marginBottom: 18 }}>
              <h2 style={{ fontSize: 20, fontWeight: 600, letterSpacing: "-0.01em", margin: "0 0 4px" }}>Resolve linked checklists</h2>
              <p style={{ fontSize: 13.5, color: "var(--fg-3)", margin: 0 }}>This process links other checklists. Every linked child must be matched, linked, or migrated before continuing.</p>
            </div>

            {discovering && (
              <div className="leu-card" style={{ padding: 64, display: "flex", flexDirection: "column", alignItems: "center", gap: 16 }}>
                <span style={{ width: 30, height: 30, border: "2.5px solid var(--bg-4)", borderTopColor: "var(--brand-500)", borderRadius: "50%", animation: "leu-spin 0.8s linear infinite" }} />
                <div style={{ textAlign: "center" }}>
                  <div style={{ fontSize: 14, fontWeight: 600 }}>Discovering linked checklists…</div>
                  <div style={{ fontSize: 12.5, color: "var(--fg-3)", marginTop: 4 }}>Walking the dependency tree on the source instance.</div>
                </div>
              </div>
            )}

            {resolveMut.isError && !discovering && (
              <div style={{ padding: 20, border: "1px solid color-mix(in oklch, var(--destructive) 35%, var(--border))", background: "color-mix(in oklch, var(--destructive) 5%, var(--bg-1))", borderRadius: "var(--radius)" }}>
                <div style={{ fontSize: 14, fontWeight: 600, color: "var(--destructive)" }}>Could not resolve linked checklists</div>
                <div style={{ fontSize: 13, color: "var(--fg-2)", marginTop: 6 }}>{resolveMut.error instanceof Error ? resolveMut.error.message : "The source or target instance rejected the request."}</div>
                <button className="leu-btn leu-btn--primary leu-btn--md" style={{ marginTop: 14 }} onClick={runResolve}><Refresh />Retry</button>
              </div>
            )}

            {discovered && (
              <div>
                <div className="leu-card" style={{ padding: "14px 18px", marginBottom: 14, display: "flex", alignItems: "center", gap: 22, flexWrap: "wrap" }}>
                  {[["var(--success)", sumPresent, "present"], ["var(--warning)", sumProposed, "proposed"], ["var(--destructive)", sumMissing, "missing"]].map(([c, num, t]) => (
                    <div key={t as string} style={{ display: "flex", alignItems: "center", gap: 8 }}><span style={{ width: 8, height: 8, borderRadius: "50%", background: c as string }} /><span style={{ fontSize: 13 }}><b style={{ fontVariantNumeric: "tabular-nums" }}>{num}</b> <span style={{ color: "var(--fg-3)" }}>{t}</span></span></div>
                  ))}
                  <div style={{ flex: 1 }} />
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <span style={{ fontSize: 12.5, color: "var(--fg-3)" }}>{resolvableNodes.length - sumUnresolved} of {resolvableNodes.length} resolved</span>
                    {allResolved && <span className="leu-badge leu-badge--success" style={{ gap: 6 }}><Check size={12} stroke={3} />All resolved</span>}
                  </div>
                </div>

                <div className="leu-card" style={{ overflow: "hidden" }}>
                  <div style={{ padding: "11px 18px", borderBottom: "1px solid var(--border)", display: "flex", alignItems: "center", justifyContent: "space-between", background: "var(--bg-2)" }}>
                    <span style={uppercaseLbl}>Dependency tree</span>
                    <span style={uppercaseLbl}>Resolution</span>
                  </div>
                  {visibleRows.map(({ node, hasChildren }) => renderRow(node, hasChildren))}
                </div>

                {renderOntologyPanel()}

                {nestedUnresolved.length > 0 && (
                  <div style={{ marginTop: 14, padding: "14px 18px", border: "1px solid color-mix(in oklch, var(--destructive) 35%, var(--border))", background: "color-mix(in oklch, var(--destructive) 5%, var(--bg-1))", borderRadius: "var(--radius)" }}>
                    <div style={{ fontSize: 14, fontWeight: 600, color: "var(--destructive)" }}>Nested linked process — more than one level is not supported</div>
                    <div style={{ fontSize: 13, color: "var(--fg-2)", marginTop: 6 }}>
                      <b>{nestedUnresolved.map((n) => n.source.name ?? n.source.id).join(", ")}</b> link{nestedUnresolved.length === 1 ? "s" : ""} further processes, so {nestedUnresolved.length === 1 ? "it" : "they"} cannot be migrated as part of this run. Migrate {nestedUnresolved.length === 1 ? "that process" : "those processes"} individually first (publishing {nestedUnresolved.length === 1 ? "it" : "them"} on the target), then come back and use <b>Link existing…</b> here.
                    </div>
                  </div>
                )}

                {missingOntologyBlocking > 0 && (
                  <div style={{ marginTop: 14, padding: "14px 18px", border: "1px solid color-mix(in oklch, var(--destructive) 35%, var(--border))", background: "color-mix(in oklch, var(--destructive) 5%, var(--bg-1))", borderRadius: "var(--radius)" }}>
                    <div style={{ fontSize: 14, fontWeight: 600, color: "var(--destructive)" }}>Migration blocked — missing ontology dependencies</div>
                    <div style={{ fontSize: 13, color: "var(--fg-2)", marginTop: 6 }}>
                      {missingOntologyBlocking} dependenc{missingOntologyBlocking === 1 ? "y" : "ies"} used by a checklist set to <b>Migrate</b> {missingOntologyBlocking === 1 ? "is" : "are"} missing on the target. Link those checklists to an existing target, or migrate the missing object types, properties and relations to the target via the Ontology Migration flow first.
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* ============ REVIEW ============ */}
        {stage === "review" && (
          <div>
            <div style={{ marginBottom: 22 }}>
              <h2 style={{ fontSize: 20, fontWeight: 600, letterSpacing: "-0.01em", margin: "0 0 4px" }}>Review migration</h2>
              <p style={{ fontSize: 13.5, color: "var(--fg-3)", margin: 0 }}>Confirm the resolved actions before migrating to the target instance.</p>
            </div>

            <div className="leu-card" style={{ padding: 18, marginBottom: 16 }}>
              <div style={{ ...uppercaseLbl, marginBottom: 12 }}>Parent process{revisionTargetId ? " · revision import" : ""}</div>
              {checklists.map((p) => (
                <div key={p.id} style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <span style={{ fontSize: 14, fontWeight: 600 }}>{p.name}</span>
                  {p.code && <span style={{ fontFamily: "var(--font-mono)", fontSize: 11.5, color: "var(--fg-3)" }}>{p.code}</span>}
                  {revisionTargetId && <span className="leu-badge leu-badge--info" style={{ height: 19, fontSize: 10 }}>Revision of {revisionTargetId}</span>}
                </div>
              ))}
              {(targetAuthorLabel || targetUseCaseId) && (
                <div style={{ marginTop: 10, fontSize: 12, color: "var(--fg-3)" }}>
                  {targetUseCaseId && <span>Target use case · <UseCaseName serviceId={targetServiceId} facilityId={targetFacilityId} useCaseId={targetUseCaseId} />&nbsp;&nbsp;</span>}
                  {targetAuthorLabel && <span>Author · {targetAuthorLabel}</span>}
                </div>
              )}
            </div>

            {resolvableNodes.length > 0 && (
              <div className="leu-card" style={{ overflow: "hidden", marginBottom: 16 }}>
                <div style={{ padding: "13px 18px", borderBottom: "1px solid var(--border)", background: "var(--bg-2)", ...uppercaseLbl }}>Resolved linked checklists</div>
                {resolvableNodes.map((n) => {
                  const a = actions[n.source.id];
                  const isMig = a?.action === "migrate";
                  const label = isMig ? "Migrate — create on target"
                    : a?.action === "link" ? "Link to existing: " + (a.linkedTo?.name ?? "") + " · " + (a.linkedTo?.code ?? "")
                      : "Link to existing: " + (n.proposedTarget?.name ?? "") + " · " + (n.proposedTarget?.code ?? "");
                  const migFacilityName = isMig
                    ? (targetFacilities.find((f) => f.id === targetFacilityId)?.name ?? targetFacilityId)
                    : null;
                  return (
                    <div key={n.source.id} style={{ display: "flex", alignItems: "center", gap: 14, padding: "12px 18px", borderBottom: "1px solid var(--border)" }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 13, fontWeight: 500 }}>{n.source.name}</div>
                        <div style={{ fontFamily: "var(--font-mono)", fontSize: 11, color: "var(--fg-3)", marginTop: 2 }}>{n.source.code}</div>
                        {isMig && a?.targetUseCaseId && (
                          <div style={{ fontSize: 11, color: "var(--fg-3)", marginTop: 3 }}>
                            Facility · <span style={{ color: "var(--fg-2)", fontWeight: 500 }}>{migFacilityName}</span>
                            &nbsp;·&nbsp;Use case · <span style={{ color: "var(--fg-2)", fontWeight: 500 }}>
                              <UseCaseName serviceId={targetServiceId} facilityId={targetFacilityId} useCaseId={a.targetUseCaseId} />
                            </span>
                          </div>
                        )}
                      </div>
                      {isMig
                        ? <span className="leu-badge leu-badge--info" style={{ gap: 6 }}><Plus size={12} />{label}</span>
                        : <span className="leu-badge leu-badge--success" style={{ gap: 6 }}><LinkIco size={12} />{label}</span>}
                    </div>
                  );
                })}
              </div>
            )}

            <div style={{ display: "flex", alignItems: "center", gap: 16, padding: "16px 18px", border: "1px solid color-mix(in oklch, var(--brand-500) 25%, var(--border))", background: "color-mix(in oklch, var(--brand-500) 4%, var(--bg-1))", borderRadius: "var(--radius)" }}>
              <span style={{ display: "inline-flex", color: "var(--brand-600)" }}><Package size={20} /></span>
              <div>
                <div style={{ fontSize: 13, fontWeight: 600, color: "var(--brand-700)" }}>Migration summary</div>
                <div style={{ fontSize: 13, color: "var(--fg-2)", marginTop: 2 }}>{decisions.filter((d) => d.action === "MIGRATE").length} new checklists will be created · {decisions.filter((d) => d.action === "LINK_EXISTING").length} linked to existing · parent process re-pointed on import{revisionTargetId ? " · parent imported as a revision" : ""}.</div>
              </div>
            </div>
          </div>
        )}

        {/* ============ DONE ============ */}
        {stage === "done" && (
          <div>
            {phase === "processing" && (
              <div className="leu-card" style={{ padding: 72, display: "flex", flexDirection: "column", alignItems: "center", gap: 18, marginTop: 24 }}>
                <span style={{ width: 34, height: 34, border: "3px solid var(--bg-4)", borderTopColor: "var(--brand-500)", borderRadius: "50%", animation: "leu-spin 0.8s linear infinite" }} />
                <div style={{ textAlign: "center" }}>
                  <div style={{ fontSize: 15, fontWeight: 600 }}>Migrating linked checklists…</div>
                  <div style={{ fontSize: 13, color: "var(--fg-3)", marginTop: 5 }}>Creating and re-pointing checklists on the target instance. Do not close this window.</div>
                </div>
              </div>
            )}

            {phase === "error" && (
              <div style={{ padding: 28, border: "1px solid color-mix(in oklch, var(--destructive) 35%, var(--border))", background: "color-mix(in oklch, var(--destructive) 5%, var(--bg-1))", borderRadius: "var(--radius)", marginTop: 24 }}>
                <div style={{ display: "flex", alignItems: "flex-start", gap: 14 }}>
                  <span style={{ display: "inline-flex", color: "var(--destructive)", flex: "none", marginTop: 2 }}><Alert size={24} stroke={1.75} /></span>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 16, fontWeight: 600, color: "var(--destructive)" }}>Migration failed</div>
                    <div style={{ fontSize: 13.5, color: "var(--fg-2)", marginTop: 6, lineHeight: 1.5 }}>{migrateError || "The target instance rejected the request. Review the resolution and retry."}</div>
                    <div style={{ display: "flex", gap: 10, marginTop: 18 }}>
                      <button className="leu-btn leu-btn--primary leu-btn--md" onClick={startMigration}><Refresh />Retry migration</button>
                      <button className="leu-btn leu-btn--outline leu-btn--md" onClick={() => { setStage("review"); setPhase("idle"); }}>Back to review</button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {phase === "done" && (() => {
              const deferred = outcomes.filter((o) => o.status === "PENDING_CHILD_PUBLISH");
              return (
                <div>
                  <div style={{ display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center", padding: "32px 0 24px" }}>
                    {deferred.length > 0 ? (
                      <>
                        <span style={{ width: 64, height: 64, borderRadius: "50%", background: "color-mix(in oklch, oklch(0.78 0.16 75) 22%, var(--card))", display: "inline-flex", alignItems: "center", justifyContent: "center", color: "oklch(0.5 0.15 70)", marginBottom: 16 }}><Clock size={32} /></span>
                        <h2 style={{ fontSize: 22, fontWeight: 600, letterSpacing: "-0.01em", margin: 0 }}>Action needed: publish linked children</h2>
                        <p style={{ fontSize: 13.5, color: "var(--fg-3)", margin: "6px 0 0", maxWidth: 520 }}>Linked child checklists were migrated as drafts. Publish them on the target instance, then continue each deferred parent from the pipeline below.</p>
                      </>
                    ) : (
                      <>
                        <span style={{ width: 64, height: 64, borderRadius: "50%", background: "color-mix(in oklch, var(--success) 14%, var(--card))", display: "inline-flex", alignItems: "center", justifyContent: "center", color: "var(--success)", marginBottom: 16 }}><Check size={34} stroke={2.5} /></span>
                        <h2 style={{ fontSize: 22, fontWeight: 600, letterSpacing: "-0.01em", margin: 0 }}>
                          {continueStandard ? "Linked group migrated" : "Migration completed"}
                        </h2>
                        <p style={{ fontSize: 13.5, color: "var(--fg-3)", margin: "6px 0 0" }}>
                          {continueStandard
                            ? `All linked checklists were resolved and migrated — ${continueStandard.count} standard checklist(s) remaining.`
                            : "All linked checklists were resolved and migrated to the target instance."}
                        </p>
                      </>
                    )}
                  </div>

                  <div className="leu-card" style={{ overflow: "hidden", marginBottom: 20 }}>
                    <div style={{ padding: "13px 18px", borderBottom: "1px solid var(--border)", background: "var(--bg-2)", ...uppercaseLbl }}>Results</div>
                    {outcomes.length === 0 && (<div style={{ padding: 24, textAlign: "center", color: "var(--fg-3)", fontSize: 13 }}>No outcomes returned.</div>)}
                    {outcomes.map((r, i) => {
                      const ref = nameLookup.get(r.sourceId);
                      return (
                        <div key={i} style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 18px", borderBottom: "1px solid var(--border)" }}>
                          {resultIcon(r.status)}
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: 13, fontWeight: 500 }}>{ref?.name ?? r.sourceId}</div>
                            <div style={{ fontFamily: "var(--font-mono)", fontSize: 11, color: "var(--fg-3)", marginTop: 2 }}>{ref?.code ?? r.sourceId}</div>
                          </div>
                          <span style={{ fontSize: 12, color: "var(--fg-3)" }}>{r.message || r.status}</span>
                        </div>
                      );
                    })}
                  </div>

                  {deferred.length > 0 && <LinkedPipelinePanel onEntryResolved={handlePipelineEntryResolved} />}

                  <div style={{ display: "flex", justifyContent: "center", gap: 12 }}>
                    {continueStandard ? (
                      <>
                        <button className="leu-btn leu-btn--lg" onClick={onStartOver}><Plus />Start new migration</button>
                        <button className="leu-btn leu-btn--primary leu-btn--lg" onClick={continueStandard.onContinue}>
                          Continue — migrate {continueStandard.count} standard checklist(s)
                        </button>
                      </>
                    ) : (
                      <button className="leu-btn leu-btn--primary leu-btn--lg" onClick={onStartOver}><Plus />Start new migration</button>
                    )}
                  </div>
                </div>
              );
            })()}
          </div>
        )}

        {/* footer nav (resolve + review) */}
        {stage !== "done" && (
          <div style={{ marginTop: 18, borderTop: "1px solid var(--border)", padding: "14px 0 0", display: "flex", alignItems: "center" }}>
            <button className="leu-btn leu-btn--outline leu-btn--md" onClick={() => (stage === "review" ? setStage("resolve") : onBack())}><ArrowLeft />Back</button>
            <div style={{ flex: 1 }} />
            <button
              className="leu-btn leu-btn--primary leu-btn--md"
              style={continueDisabled ? { opacity: 0.45, pointerEvents: "none" } : undefined}
              onClick={() => (stage === "resolve" ? setStage("review") : startMigration())}
            >
              {stage === "resolve" ? "Continue" : "Migrate"}<ArrowRight />
            </button>
          </div>
        )}

        {/* ---- Link-existing dialog (search target checklists) ---- */}
        {dialogFor && (
          <div style={{ position: "fixed", inset: 0, zIndex: 50, background: "rgba(0,0,0,0.5)", backdropFilter: "blur(3px)", display: "flex", alignItems: "flex-start", justifyContent: "center", paddingTop: "12vh" }} onClick={closeDialog}>
            <div className="leu-card" style={{ width: 560, maxWidth: "92vw", boxShadow: "var(--shadow-xl)", overflow: "hidden" }} onClick={(e) => e.stopPropagation()}>
              <div style={{ padding: "12px 14px", borderBottom: "1px solid var(--border)", display: "flex", alignItems: "center", gap: 10 }}>
                <span style={{ color: "var(--fg-3)", display: "inline-flex" }}><SearchIco size={16} /></span>
                <input autoFocus className="leu-input" style={{ border: 0, boxShadow: "none", padding: 0, height: 24, fontSize: 14 }} placeholder="Search all target checklists by name…" value={dialogSearch} onChange={(e) => setDialogSearch(e.target.value)} />
                <select
                  className="leu-input"
                  style={{ height: 26, fontSize: 12, padding: "0 6px", maxWidth: 180 }}
                  value={dialogUseCaseFilter}
                  onChange={(e) => setDialogUseCaseFilter(e.target.value)}
                  title="Filter by use case"
                >
                  <option value="">All use cases</option>
                  {targetUseCases.map((u) => (
                    <option key={u.id} value={u.id}>{u.name}</option>
                  ))}
                </select>
                <button onClick={closeDialog} style={{ fontFamily: "var(--font-mono)", fontSize: 10.5, color: "var(--fg-4)", border: "1px solid var(--border)", borderRadius: 5, padding: "2px 6px", background: "transparent", cursor: "pointer" }}>ESC</button>
              </div>
              <div style={{ padding: "6px 14px", fontSize: 11, color: "var(--fg-3)" }}>{(() => { const ref = nameLookup.get(dialogFor); return ref ? `Choose a target checklist to link for "${ref.name}"` : ""; })()}</div>
              <div style={{ maxHeight: 340, overflow: "auto", padding: 4 }}>
                <div style={{ fontSize: 9.5, letterSpacing: "0.06em", textTransform: "uppercase", color: "var(--fg-4)", fontWeight: 500, padding: "8px 10px 4px" }}>Target checklists · {(dialogResultsQuery.data ?? []).length}</div>
                {dialogResultsQuery.isLoading && (<div style={{ padding: 20, textAlign: "center", color: "var(--fg-3)", fontSize: 12.5 }}>Searching…</div>)}
                {(dialogResultsQuery.data ?? []).map((d) => (
                  <div key={d.id} className="leu-dlg-item" onClick={() => pickLinked(d)} style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 10px", borderRadius: 7, cursor: "pointer" }}>
                    <span style={{ width: 28, height: 28, borderRadius: 7, background: "var(--bg-3)", color: "var(--fg-3)", display: "inline-flex", alignItems: "center", justifyContent: "center", flex: "none" }}><ListIco size={15} /></span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 500 }}>{d.name}</div>
                      <div style={{ fontFamily: "var(--font-mono)", fontSize: 11, color: "var(--fg-3)", marginTop: 1 }}>{d.code}{d.useCaseName ? ` · ${d.useCaseName}` : ""}</div>
                    </div>
                    <span style={{ color: "var(--fg-4)", display: "inline-flex" }}><ChevronRight /></span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default LinkedResolutionFlow;
